import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'
import { Link } from 'react-router-dom'

import useDeferredCaptchaSubmit from 'hooks/useDeferredCaptchaSubmit'
import {
  dispatchRequestAccountCode,
  dispatchVerifyOtp
} from 'reduxModules/auth/dispatchers'
import { registerBoxSelector } from 'reduxModules/auth/selectors'
import useNewsletterDefault from 'hooks/useNewsletterDefault'
import NewsletterConsent from '../NewsletterConsent'
import SocialButtons from '../SocialButtons'
import OtpCodeStep from '../OtpCodeStep'
import { AuthFooter, AuthTitle, OrDivider, TermsLine } from '../AuthParts'
import CaptchaStandalone from '../../CaptchaStandalone/_lazy'
import EMAIL_PATTERN from '../../utils/emailPattern'
import InlineError, { ariaErrorProps } from '../InlineError'
import '../RenderedField/styles.css'
import './styles.css'

/**
 * SPEC-101: the passwordless "Enter your email to create your account" screen
 * (register page, checkout sign-up, plans-page step). The heading names the
 * primary action (D12); an existing email is still taken care of and goes to
 * the "Welcome back" code step.
 *
 *   start — email, the social sign-in stack, the optional newsletter box (it
 *           covers every option), the terms line and Continue. A new email
 *           creates the account right there (D10) and carries on, to payment
 *           in checkout.
 *   code  — an email that already has an account gets a login code instead
 *           (D5/D7), entered here, and then carries on the same way.
 */
export function RegisterBox({
  autoFocus = false,
  inCheckout = false,
  isGlobalPromoEnabled,
  promoAppliesToSelectedPlan = true,
  planOffersTrial = true,
  planIsOneTimePurchase = false,
  globalPromo,
  location,
  isMobile,
  recaptcha,
  requestCode,
  verifyCode
}) {
  const showPromo = Boolean(isGlobalPromoEnabled && promoAppliesToSelectedPlan)
  const [step, setStep] = useState('start')
  // The address and case (signup | login) of the last code sent; the code
  // step reads these, so editing the field afterwards can't desync them
  const [sent, setSent] = useState({ email: '', flow: 'signup' })
  // Each successful send restarts the code step's resend cooldown
  const [sendCount, setSendCount] = useState(0)

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({ defaultValues: { email: '', marketingConsent: false } })
  // SPEC-109: the box starts unticked and is ticked from the backend's
  // per-country default unless the person has touched it (signed-out only)
  const { markTouched, touched } = useNewsletterDefault({ setValue, getValues })

  // SPEC-099: the optional newsletter opt-in. It goes with the code request
  // and with the social tiles (they create the account in one click).
  const marketingConsent = Boolean(watch('marketingConsent'))
  const emailOk = EMAIL_PATTERN.test(watch('email') || '')

  // Validate the email inline: re-check on every keystroke (so the format error
  // shows as the user types, even when the field is autofocused and never
  // blurred) and on blur — instead of waiting for submit. Leaving it empty
  // isn't an error here: the field is autofocused, and someone heading for a
  // social tile or the box shouldn't be told it's required (Continue stays
  // disabled until the address is valid).
  const emailField = register('email', {
    required: 'This field is required.',
    pattern: {
      value: EMAIL_PATTERN,
      message: 'Invalid email address.'
    }
  })
  const handleEmailBlur = (event) => {
    emailField.onBlur(event)
    if (event.target.value) trigger('email')
  }
  const handleEmailChange = (event) => {
    emailField.onChange(event)
    trigger('email')
  }

  const sendCode = ({ email, marketingConsent: consent }) =>
    requestCode({
      email,
      marketingConsent: Boolean(consent),
      marketingConsentTouched: touched.current
    }).then((result) => {
      if (result && result.sent) {
        setSent({ email, flow: result.flow })
        setStep('code')
        setSendCount((count) => count + 1)
      }
      return result
    })

  // Gate the request on Turnstile: capture an early click and replay it once
  // the token lands, and keep the form locked until the request settles.
  const { submit, awaitingVerification, isSubmitting } =
    useDeferredCaptchaSubmit(recaptcha.token, sendCode)
  const sending = isSubmitting || awaitingVerification

  const verify = (email, code) =>
    verifyCode(
      { email, code },
      {
        flow: sent.flow,
        trigger: 'create-account',
        marketingConsent,
        marketingConsentTouched: touched.current,
        inCheckout,
        isMobile,
        location
      }
    )

  return (
    <div id="RegisterBox">
      <div
        className={classnames('sign-up-box auth-form-box', {
          '-code-step': step === 'code'
        })}
      >
        {step === 'code' && (
          <OtpCodeStep
            sentTo={sent.email}
            sendCount={sendCount}
            title="Welcome back"
            subtitle={
              <>
                <strong>{sent.email}</strong> already has an account. Enter the
                6-digit code we sent to log in.
              </>
            }
            submitLabel="Log in"
            verifyCode={verify}
            onResend={() => submit({ email: sent.email, marketingConsent })}
            onBack={() => setStep('start')}
            captchaAppearance="interaction-only"
            sending={sending}
            canResend={Boolean(recaptcha.token)}
          />
        )}
        {step === 'start' && (
          <>
            {inCheckout && (
              <div className="title">
                <div className="free-trial-text">
                  {!showPromo && planOffersTrial && (
                    <h3>Sign Up To Start Your Free 7-Day Trial</h3>
                  )}
                  {showPromo && (
                    <h3>
                      {planIsOneTimePurchase ? 'Purchase' : 'Subscribe'} and
                      Save {globalPromo.discount}% with code:{' '}
                      {globalPromo.codename}
                    </h3>
                  )}
                </div>
                <div className="create-account">
                  Enter your email to create your account
                </div>
              </div>
            )}
            {!inCheckout && (
              <AuthTitle>Enter your email to create your account</AuthTitle>
            )}
            <form onSubmit={handleSubmit(submit)} noValidate>
              <div
                className={classnames('field', '-floating', {
                  '-error': errors.email
                })}
              >
                <div className="field-container">
                  <input
                    {...emailField}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    id="register-email"
                    type="email"
                    placeholder=" "
                    autoFocus={autoFocus}
                    {...ariaErrorProps('register-email', errors.email)}
                  />
                  <label htmlFor="register-email">Email</label>
                  <InlineError
                    id="register-email"
                    message={errors.email?.message}
                  />
                </div>
              </div>

              <OrDivider />
              <SocialButtons
                signUp
                location={location}
                inCheckout={inCheckout}
                isMobile={isMobile}
                marketingConsent={marketingConsent}
                marketingConsentTouched={touched.current}
                showOpenAthens={!inCheckout}
              />

              <NewsletterConsent
                id={
                  inCheckout
                    ? 'register-newsletter-checkout'
                    : 'register-newsletter'
                }
                inputProps={register('marketingConsent')}
                onTouched={markTouched}
                privacyLink={false}
              />
              <TermsLine />
              {/* Invisible unless Cloudflare wants an interaction */}
              <CaptchaStandalone appearance="interaction-only" />
              <button
                type="submit"
                // SPEC-101 D9: the primary button is orange on every auth screen
                className="btn -wide -orange"
                disabled={!emailOk || sending}
                style={{ width: '100%' }}
              >
                {sending ? 'Sending…' : 'Continue'}
              </button>
            </form>
            {!inCheckout && (
              <AuthFooter>
                Already have an account? <Link to="/login">Log in</Link>
              </AuthFooter>
            )}
          </>
        )}
      </div>
    </div>
  )
}

RegisterBox.propTypes = {
  requestCode: PropTypes.func.isRequired,
  verifyCode: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  inCheckout: PropTypes.bool,
  isGlobalPromoEnabled: PropTypes.bool,
  promoAppliesToSelectedPlan: PropTypes.bool,
  planOffersTrial: PropTypes.bool,
  planIsOneTimePurchase: PropTypes.bool,
  globalPromo: PropTypes.shape({
    discount: PropTypes.number,
    codename: PropTypes.string
  }),
  location: PropTypes.object,
  isMobile: PropTypes.bool,
  recaptcha: PropTypes.shape({
    token: PropTypes.string,
    version: PropTypes.string
  })
}

export default connect(
  registerBoxSelector,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const { dispatch } = dispatchProps
    return {
      ...stateProps,
      ...ownProps,
      requestCode: ({ email, marketingConsent, marketingConsentTouched }) =>
        dispatchRequestAccountCode(
          dispatch,
          {
            email,
            marketingConsent,
            marketingConsentTouched,
            recaptcha: stateProps.recaptcha
          },
          ownProps
        ),
      verifyCode: (data, props) =>
        dispatchVerifyOtp(dispatch, data, { ...ownProps, ...props })
    }
  }
)(RegisterBox)
